import type { AxiosProgressEvent } from 'axios';
import axios from 'axios';

import type { BulkUploadResponse } from '@apple/utils/files/models/bulk-upload';

import type { StoreBulkUploadItem, StoreFormData, StoreModel } from '../models';
import {
	storeBulkUploadResponseSchema,
	storeFormDataSchema,
	storeModelRequestSchema,
	storeModelSchema,
} from '../models';

function getStoreFormDataModel(apiStore: StoreModel): StoreFormData {
	return {
		...apiStore,
		recipient: apiStore.recipientName,
		storeId: apiStore.thirdPartyCustomerId,
		storeName: apiStore.name,
		addressLineOne: apiStore.streetAddress1,
		addressLineTwo: apiStore.streetAddress2,
		addressLineThree: apiStore.streetAddress3,
		stateProvinceRegion: apiStore.stateOrProvince,
		country: apiStore.countryCode,
		phoneNumber: apiStore.phone ?? '',
		imageFileName: apiStore.logoImageUrl,
		locationType: apiStore.customerClassification,
	};
}

/**
 * Gets a store with the given customer code
 * @see AppleRetail.Web.Features.Stores.StoreManagementController
 */
export async function getStore(storeCode: string, signal?: AbortSignal): Promise<StoreFormData> {
	const response = (await axios.get<unknown>(`api/management/stores/${storeCode}`, { signal }))
		.data;
	const storeDataSchema = storeModelSchema.parse(response);
	const storeFormData = getStoreFormDataModel(storeDataSchema);

	return storeFormDataSchema.parse(storeFormData);
}

/**
 * Creates a new store.
 * @param storeModel - The store to create.
 * @see AppleRetail.Web.Features.Stores.StoreManagementController
 */
export async function addStore(storeModel: StoreFormData): Promise<void> {
	const model = getStoreModel(storeModel);
	await axios.post('/api/management/stores/add', model);
}

/**
 * Saves an existing store.
 * @param storeModel - The store to save.
 * @see AppleRetail.Web.Features.Stores.StoreManagementController
 */
export async function saveStore(storeModel: StoreFormData): Promise<void> {
	const model = getStoreModel(storeModel);
	await axios.put('/api/management/stores/save', model);
}

/**
 * Deletes an existing store.
 * @param storeCode - The customer code of the store to delete.
 * @see AppleRetail.Web.Features.Stores.StoreManagementController
 */
export async function deleteStore(storeCode: string): Promise<void> {
	await axios.delete(`/api/management/stores/${storeCode}`);
}

/**
 * Uploads the store image.
 * @param storeCode - Customer code of the store.
 * @see AppleRetail.Web.Features.Stores.StoreManagementController
 */
export async function uploadImage(selectedFile: File, storeCode: string): Promise<string> {
	const formData = new FormData();
	formData.append('file', selectedFile);
	formData.append('storeId', storeCode);

	const response = await axios.post<string>('/api/management/stores/images', formData, {
		headers: { 'Content-Type': 'multipart/form-data' },
	});
	return response.data;
}

/** @see AppleRetail.Web.Features.Budgets.BudgetReportDetailsController */
export function getBudgetReportUrl(): string {
	return '/api/budget-report/all-stores';
}

function getStoreModel(store: StoreFormData) {
	const currentCountry = store.country;
	if (currentCountry !== 'BR') {
		store.cnpj = '';
		store.ie = '';
		store.isIeExempt = false;
	}
	return storeModelRequestSchema.parse({
		...store,
		id: store.id === '' ? null : store.id,
		customerClassification: store.locationType,
		recipientName: store.recipient,
		thirdPartyCustomerId: store.storeId,
		name: store.storeName,
		streetAddress1: store.addressLineOne,
		streetAddress2: store.addressLineTwo,
		streetAddress3: store.addressLineThree,
		stateOrProvince: store.stateProvinceRegion,
		countryCode: store.country,
		phone: store.phoneNumber,
		logoImageUrl: store.imageFileName,
	});
}

/**
 * Uploads a store budget template Excel file.
 * @see AppleRetail.Web.Features.Stores.StoreBulkManagementApiController
 */
export async function uploadStoreBulkTemplate(
	file: File,
	onUploadProgress?: (event: AxiosProgressEvent) => void,
): Promise<BulkUploadResponse<StoreBulkUploadItem>> {
	const body = new FormData();
	body.append('file', file);
	body.append('RejectOnAnyError', 'true');

	const response = await axios.put<unknown>('/api/management/stores/bulk/excel', body, {
		onUploadProgress,
	});

	return storeBulkUploadResponseSchema.parse(response.data);
}

/**
 * Saves uploaded budget
@see AppleRetail.Web.Features.Stores.StoreBulkManagementApiController
*/
export async function saveBudgets(fileId: number): Promise<void> {
	await axios.post(`api/management/stores/bulk/excel/${fileId}`);
}
